import { useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  DEFAULT_SETTINGS,
  ZONA_HORARIA,
  formatMoney,
  formatQuantity,
  parseMoney,
  parseQuantity,
  ubicacionCamion,
  type Centavos,
} from '@abastece/shared';
import { efectivoEsperado, fiadoDeVenta } from '@abastece/core';
import { db } from '../db/dexie.js';
import { readSettings } from '../db/reader.js';
import { emitirYVer } from '../lib/emitir.js';
import { atrasHasta } from '../lib/volver.js';
import { bloquear, usePuede } from '../lib/sesion.js';
import { Campo } from '../ui/Campo.js';
import { Dato, Veredicto, enDolares } from '../ui/CuentasDeCaja.js';
import { Ico } from '../ui/Iconos.js';
import { useEmpiezaElDia } from '../ui/LoDelDia.js';
import { Pagina } from '../ui/Pagina.js';
import { localeActual, useT } from '../i18n/index.js';
import { useNombreProducto } from '../i18n/productos.js';

interface PorClienteFila {
  clienteId: string;
  monto: Centavos;
}

/** Amounts added up per client, biggest first. Rows with no client or nothing in them drop out. */
function porCliente(filas: readonly { clienteId: string | null; monto: number }[]): PorClienteFila[] {
  const suma = new Map<string, number>();
  for (const f of filas) {
    if (f.clienteId && f.monto > 0) suma.set(f.clienteId, (suma.get(f.clienteId) ?? 0) + f.monto);
  }
  return [...suma]
    .map(([clienteId, monto]) => ({ clienteId, monto: monto as Centavos }))
    .sort((a, b) => b.monto - a.monto);
}

function ListaPorCliente({ filas, nombre }: { filas: PorClienteFila[]; nombre: (id: string) => string }) {
  return (
    <ul className="flex flex-col gap-1 text-sm">
      {filas.map((f) => (
        <li key={f.clienteId} className="flex justify-between gap-3">
          <span className="truncate">{nombre(f.clienteId)}</span>
          <span className="tabular shrink-0 text-slate-400">{formatMoney(f.monto)}</span>
        </li>
      ))}
    </ul>
  );
}

/**
 * Liquidar una ruta de antes.
 *
 * The owner took «Reparto» out on 2026-10-04 («it's too much»): no trucks to load, no route to
 * settle; whoever delivers charges the orders at a till of their own. A route day already out
 * then still has its till open at the truck, and that till closes only with the route's settlement
 * (`caja.cerrada` is refused for it, see `projections/reparto.ts`). This is what is left of the
 * route screen, for that: «Terminar mi turno» on such a till comes here (`TerminarTurno`), the cash
 * is counted, what is still on the truck is counted and goes back to the storeroom, and the till
 * closes with it, in one event as it always did. Nothing here opens a route, and once none is open
 * nothing leads here.
 *
 * Settling is the owner's (`liquidar_ruta`), counted with whoever had the route, on the phone the
 * till is on: the owner's events count only from a device the owner is signed in on. Anybody else
 * is told so, and hands the phone over from here.
 */
export function LiquidarRuta({ jornadaId }: { jornadaId: string }) {
  const t = useT();
  const nombre = useNombreProducto();
  const puedeLiquidar = usePuede('liquidar_ruta');
  const empiezaElDia = useEmpiezaElDia();
  const config = useLiveQuery(() => readSettings(), [], DEFAULT_SETTINGS);
  const jornada = useLiveQuery(() => db.jornadas.get(jornadaId), [jornadaId]);
  const caja = useLiveQuery(async () => (jornada ? await db.sesiones.get(jornada.sesionId) : undefined), [jornada?.sesionId]);
  /**
   * What is on this route's truck, said with the truck it was read for. Until it is this one's, the
   * form waits: an empty list on screen reads «nothing left on the truck», and a settlement sent then
   * would count every unit still on it as missing.
   */
  const leidoDelCamion = useLiveQuery(
    async () =>
      jornada
        ? {
            camion: jornada.camion,
            filas: (await db.existencias.toArray()).filter((e) => e.ubicacion === jornada.camion && e.cantidad !== 0),
          }
        : undefined,
    [jornada?.camion],
  );
  const enCamion = jornada && leidoDelCamion?.camion === jornada.camion ? leidoDelCamion.filas : undefined;
  const productos = useLiveQuery(() => db.productos.toArray(), [], []);
  const clientes = useLiveQuery(() => db.clientes.toArray(), [], []);
  const ventas = useLiveQuery(() => (caja ? db.ventas.where('sesionId').equals(caja.id).toArray() : []), [caja?.id], []);
  const abonos = useLiveQuery(() => (caja ? db.abonos.filter((a) => a.sesionId === caja.id).toArray() : []), [caja?.id], []);

  const [conteo, setConteo] = useState<Record<string, string>>({});
  const [efectivo, setEfectivo] = useState('');
  const [dolares, setDolares] = useState('');
  const [nota, setNota] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!jornada || !caja || !enCamion) return <Pagina titulo={t('inicio.liquidarRuta')}>{null}</Pagina>;

  const camion = config.camiones.find((c) => ubicacionCamion(c.id) === jornada.camion)?.nombre ?? t('reparto.camion');
  const desde = new Date(`${jornada.fecha}T12:00:00Z`).toLocaleDateString(localeActual(), {
    day: 'numeric',
    month: 'long',
    timeZone: ZONA_HORARIA,
  });
  const subtitulo = `${camion} · ${t('reparto.desdeEl', { fecha: desde })}`;
  const nombreProducto = (id: string) => {
    const p = productos.find((x) => x.id === id);
    return p ? nombre(p) : id;
  };
  const nombreCliente = (id: string) => clientes.find((c) => c.id === id)?.nombre ?? id;

  // --- Settled: what it found, and the till to start, now there is none open here. ---
  if (jornada.estado === 'liquidada') {
    const contadoC = caja.conteoCordobas ?? (0 as Centavos);
    const contadoD = caja.conteoDolares ?? (0 as Centavos);
    const esperadoC = (contadoC - (caja.diferenciaCordobas ?? 0)) as Centavos;
    const esperadoD = (contadoD - (caja.diferenciaDolares ?? 0)) as Centavos;
    const conDolares = esperadoD > 0 || contadoD > 0;
    return (
      <Pagina titulo={t('reparto.liquidada')} subtitulo={subtitulo} volverA="/">
        <section className="tarjeta flex flex-col gap-5" data-ruta-liquidada>
          <Veredicto
            esperado={t('caja.esperadoCordobas')}
            valorEsperado={formatMoney(esperadoC)}
            valorContado={formatMoney(contadoC)}
            diferencia={(contadoC - esperadoC) as Centavos}
            cuadra={t(conDolares ? 'caja.cordobasCuadran' : 'caja.cuadraExacto')}
            formato={(c) => formatMoney(c)}
          />
          {conDolares && (
            <Veredicto
              esperado={t('caja.esperadoDolares')}
              valorEsperado={enDolares(esperadoD)}
              valorContado={enDolares(contadoD)}
              diferencia={(contadoD - esperadoD) as Centavos}
              cuadra={t('caja.dolaresCuadran')}
              formato={(c) => enDolares(c)}
            />
          )}
          <div className="flex flex-col gap-2">
            <Dato etiqueta={t('reparto.devuelto')} valor={formatMoney(jornada.valorDevuelto)} />
            {jornada.faltanteMercaderia > 0 && (
              <Dato etiqueta={t('reparto.faltanteMercaderia')} valor={formatMoney(jornada.faltanteMercaderia)} />
            )}
          </div>
          <p className="text-sm text-slate-400">{t('reparto.liquidadaExplicacion')}</p>
        </section>
        <div className="mt-4 flex flex-col gap-3">
          <Link to="/caja/empezar" className="btn-primario">
            <Ico icono="caja" />
            {t(empiezaElDia === false ? 'turno.empezarTurno' : 'turno.empezarDia')}
          </Link>
          <Link to="/" replace onClick={atrasHasta('/')} className="btn-secundario">
            <Ico icono="inicio" />
            {t('turno.irAlInicio')}
          </Link>
        </div>
      </Pagina>
    );
  }

  // --- Somebody who may not settle: the owner does, here, on this phone. ---
  if (!puedeLiquidar) {
    return (
      <Pagina titulo={t('inicio.liquidarRuta')} subtitulo={subtitulo} volverA="/caja">
        <section className="tarjeta flex flex-col gap-4">
          <p className="text-sm text-slate-400">{t('reparto.deAntes')}</p>
          <p className="text-sm">{t('reparto.loLiquidaElDueno')}</p>
          <button type="button" className="btn-primario" onClick={bloquear}>
            <Ico icono="bloquear" />
            {t('reparto.pasarAlDueno')}
          </button>
        </section>
      </Pagina>
    );
  }

  /**
   * Who took fiado on this route, and who paid some back, client by client. Without it the owner
   * settles from the count alone, and a cash sale written down as fiado for a client with room on
   * their limit squares every figure here: the driver keeps the cash, and the client finds out at
   * the next statement.
   */
  const fiadoRuta = porCliente(ventas.filter((v) => !v.anulada).map((v) => ({ clienteId: v.clienteId, monto: fiadoDeVenta(v) })));
  const abonosRuta = porCliente(abonos.filter((a) => !a.anulado).map((a) => ({ clienteId: a.clienteId, monto: a.monto })));

  async function liquidar() {
    if (!jornada || !caja || !enCamion) return;
    const contado = parseMoney(efectivo);
    if (contado === null) {
      setError(t('reparto.cuenteEfectivo'));
      return;
    }
    // Typed and unreadable is not none: a count read as nothing charged the driver for all of it.
    const conteoCamion = enCamion.map((e) => ({
      productoId: e.productoId,
      cantidad: (conteo[e.productoId] ?? '').trim() === '' ? 0 : parseQuantity(conteo[e.productoId]!),
    }));
    const ilegible = conteoCamion.find((c) => c.cantidad === null || c.cantidad < 0);
    if (ilegible) {
      setError(t('reparto.conteoIlegible', { producto: nombreProducto(ilegible.productoId) }));
      return;
    }
    const conteoDolares = dolares.trim() === '' ? 0 : parseMoney(dolares);
    if (conteoDolares === null || conteoDolares < 0) {
      setError(t('reparto.dolaresIlegibles'));
      return;
    }
    setError(null);
    setTrabajando(true);
    try {
      const { rechazo } = await emitirYVer('jornada.liquidada', {
        jornadaId: jornada.id,
        // A product nobody counted counts as none (`projections/reparto.ts`): it did not come back.
        conteoCamion: conteoCamion as { productoId: string; cantidad: number }[],
        conteoEfectivo: contado,
        conteoDolares,
        tasaCambio: config.fxTasaOficial,
        // What comes back off the truck goes to the shop, the one place there is (the owner, 2026-10-05).
        ubicacionRetorno: 'tienda',
        ...(nota.trim() ? { nota: nota.trim() } : {}),
      });
      // Refused: said here, and nothing claims the route was settled.
      if (rechazo) setError(rechazo);
    } catch (e) {
      setError(e instanceof Error ? e.message : t('reparto.noSePudoLiquidar'));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Pagina titulo={t('inicio.liquidarRuta')} subtitulo={subtitulo} volverA="/caja">
      <section className="tarjeta flex flex-col gap-4">
        <p className="text-sm text-slate-400">{t('reparto.deAntes')}</p>

        <h2 className="text-sm font-semibold text-slate-400">{t('reparto.vaEnElCamion')}</h2>
        {enCamion.length === 0 ? (
          <p className="text-sm text-slate-500">{t('reparto.camionVacio')}</p>
        ) : (
          <>
            <p className="text-xs text-slate-500">{t('reparto.explicacionLiquidacion')}</p>
            {enCamion.map((e) => (
              <Campo
                key={e.id}
                etiqueta={nombreProducto(e.productoId)}
                inputMode="decimal"
                className="tabular"
                value={conteo[e.productoId] ?? ''}
                onChange={(ev) => setConteo((c) => ({ ...c, [e.productoId]: ev.target.value }))}
                placeholder="0"
                ayuda={
                  config.cajaConteoCiego
                    ? t('reparto.contadoEn')
                    : t('reparto.sistemaTiene', { cantidad: formatQuantity(e.cantidad) })
                }
              />
            ))}
          </>
        )}

        {fiadoRuta.length > 0 && (
          <div className="flex flex-col gap-1">
            <h3 className="text-sm text-slate-400">{t('reparto.fiadoEnRuta')}</h3>
            <p className="text-xs text-slate-500">{t('reparto.fiadoEnRutaAyuda')}</p>
            <ListaPorCliente filas={fiadoRuta} nombre={nombreCliente} />
          </div>
        )}
        {abonosRuta.length > 0 && (
          <div className="flex flex-col gap-1">
            <h3 className="text-sm text-slate-400">{t('reparto.abonosEnRuta')}</h3>
            <ListaPorCliente filas={abonosRuta} nombre={nombreCliente} />
          </div>
        )}
        {(caja.pagado ?? 0) > 0 && (
          <p className="text-sm text-slate-400">{t('reparto.pagadoEnRutaTotal', { monto: formatMoney(caja.pagado!) })}</p>
        )}

        <Campo
          etiqueta={t('reparto.efectivoContado')}
          inputMode="decimal"
          className="tabular text-lg"
          value={efectivo}
          onChange={(e) => setEfectivo(e.target.value)}
          placeholder="0.00"
          ayuda={
            config.cajaConteoCiego
              ? t('reparto.incluyaFondo')
              : t('reparto.deberiaHaber', { monto: formatMoney(efectivoEsperado(caja)) })
          }
        />
        <Campo
          etiqueta={t('reparto.dolaresContados')}
          inputMode="decimal"
          className="tabular"
          value={dolares}
          onChange={(e) => setDolares(e.target.value)}
          placeholder="0.00"
        />
        <Campo etiqueta={t('comun.nota')} value={nota} onChange={(e) => setNota(e.target.value)} autoComplete="off" maxLength={500} />

        {error && <p className="text-sm text-rose-400">{error}</p>}

        <button
          type="button"
          className="btn-primario"
          disabled={trabajando || parseMoney(efectivo) === null}
          onClick={() => void liquidar()}
        >
          <Ico icono="liquidar" />
          {t(trabajando ? 'reparto.liquidando' : 'inicio.liquidarRuta')}
        </button>
        <p className="text-xs text-slate-500">{t('reparto.explicacionCierre')}</p>
      </section>
    </Pagina>
  );
}
